import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  CircularProgress,
  Alert,
  Card,
  CardContent,
  Box,
  Paper,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  SelectChangeEvent,
  Stack,
  Chip,
  Divider,
  Tooltip,
  alpha,
} from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import { useApi } from '../hooks/useApi';
import { cyber } from '../theme';

// Import icons
import BoltIcon from '@mui/icons-material/Bolt';
import TargetIcon from '@mui/icons-material/TrackChanges';
import FeedIcon from '@mui/icons-material/Feed';
import SecurityIcon from '@mui/icons-material/Security';
import BusinessIcon from '@mui/icons-material/Business';
import AnalystIcon from '@mui/icons-material/PersonSearch';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import BugReportIcon from '@mui/icons-material/BugReport';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import TrendingFlatIcon from '@mui/icons-material/TrendingFlat';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import StarIcon from '@mui/icons-material/Star';
import LightbulbIcon from '@mui/icons-material/Lightbulb';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import ShieldIcon from '@mui/icons-material/Shield';
import FingerprintIcon from '@mui/icons-material/Fingerprint';
import AccountTreeIcon from '@mui/icons-material/AccountTree';
import HubIcon from '@mui/icons-material/Hub';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  ResponsiveContainer,
  Cell,
} from 'recharts';

// =============================================================================
// TYPES (matching the new JSON schema)
// =============================================================================

interface SnapshotTTP {
  name: string;
  description?: string;
  frequency: number;
  trend: 'increasing' | 'stable' | 'decreasing';
}

interface SectorStat {
  name: string;
  count: number;
  percentage: number;
}

interface SnapshotSection {
  top_ttps: SnapshotTTP[];
  top_sectors: SectorStat[];
  emerging_pattern?: string;
}

interface DeepDiveSection {
  report_id: string;
  title: string;
  actor: string;
  threat: string;
  key_ttps: string;
  impact: string;
  analyst_note: string;
  selection_rationale: string;
  original_source?: string;
}

interface SpotlightCVE {
  cve_id: string;
  severity: string;
  cvss_score?: number;
  why_notable: string;
  action_required: string;
  related_reports: string[];
}

interface VulnerabilitySpotlightSection {
  total_cves: number;
  severity_breakdown: Record<string, number>;
  exploited_in_wild: number;
  spotlight_cve?: SpotlightCVE;
  top_vendors_affected: string[];
}

interface FeedItem {
  report_id: string;
  title: string;
  actor: string;
  threat: string;
  severity_score: number;
}

interface HonorableMention {
  report_id: string;
  title: string;
  one_liner: string;
}

interface ExecutiveSummary {
  key_takeaway: string;
  look_ahead: string;
}

interface MetaSection {
  week_of: string;
  generated_at: string;
  total_reports_analyzed: number;
}

interface PlatformStats {
  reports_this_week: number;
  cves_tracked: number;
  iocs_extracted: number;
}

interface TopIOCToMonitor {
  type: string;
  value: string;
  context: string;
}

interface IOCSummarySection {
  total_iocs: number;
  ioc_type_breakdown: Record<string, number>;
  shared_infrastructure_count: number;
  top_iocs_to_monitor: TopIOCToMonitor[];
  campaign_links: string[];
}

interface TopTechnique {
  technique_id: string;
  technique_name: string;
  tactic: string;
  frequency: number;
  report_count: number;
}

interface MITRECoverageSection {
  total_techniques: number;
  top_techniques: TopTechnique[];
  tactic_distribution: Record<string, number>;
  attack_chain_summary: string;
  defensive_recommendations: string[];
}

interface WeeklyBriefing {
  meta: MetaSection;
  executive_summary: ExecutiveSummary;
  snapshot: SnapshotSection;
  deep_dive: DeepDiveSection;
  vulnerability_spotlight: VulnerabilitySpotlightSection;
  feed: FeedItem[];
  honorable_mentions: HonorableMention[];
  platform_stats: PlatformStats;
  ioc_summary?: IOCSummarySection;
  mitre_coverage?: MITRECoverageSection;
}

interface BriefingInfo {
  date: string;
  file_name: string;
}

// =============================================================================
// NORMALIZATION FUNCTION (supports both old and new formats)
// =============================================================================

const normalizeBriefing = (data: any): WeeklyBriefing => {
  // Check if it's the new format (has 'meta' and 'executive_summary')
  const isNewFormat = data.meta && data.executive_summary;

  if (isNewFormat) {
    // New format - return as is
    return data as WeeklyBriefing;
  }

  // Old format - convert to new format
  console.log('Legacy briefing detected, normalizing to new format...');

  return {
    meta: {
      week_of: data.week_of || 'Unknown',
      generated_at: new Date().toISOString(),
      total_reports_analyzed: data.total_threats || 0,
    },
    executive_summary: {
      key_takeaway: 'Legacy report - executive summary not available for older briefings.',
      look_ahead: 'Please refer to the detailed threat analysis and feed below for insights.',
    },
    snapshot: {
      top_ttps:
        Array.isArray(data.snapshot?.top_ttps) && typeof data.snapshot.top_ttps[0] === 'string'
          ? data.snapshot.top_ttps.map((name: string) => ({
              name,
              description: '',
              frequency: 0,
              trend: 'stable' as const,
            }))
          : data.snapshot?.top_ttps || [],
      top_sectors:
        Array.isArray(data.snapshot?.top_sectors) && typeof data.snapshot.top_sectors[0] === 'string'
          ? data.snapshot.top_sectors.map((name: string) => ({
              name,
              count: 0,
              percentage: 0,
            }))
          : data.snapshot?.top_sectors || [],
      emerging_pattern: data.snapshot?.emerging_pattern,
    },
    deep_dive: {
      report_id: data.deep_dive?.source_report_id || data.deep_dive?.report_id || '',
      title: data.deep_dive?.title || '',
      actor: data.deep_dive?.actor || '',
      threat: data.deep_dive?.threat || '',
      key_ttps: data.deep_dive?.key_ttps || '',
      impact: data.deep_dive?.impact || '',
      analyst_note: data.deep_dive?.analyst_note || '',
      selection_rationale: data.deep_dive?.selection_rationale || 'N/A (legacy report)',
      original_source: data.deep_dive?.original_source || '',
    },
    vulnerability_spotlight: data.vulnerability_spotlight || {
      total_cves: 0,
      severity_breakdown: {},
      exploited_in_wild: 0,
      spotlight_cve: undefined,
      top_vendors_affected: [],
    },
    feed: Array.isArray(data.feed)
      ? data.feed.map((item: any) => ({
          report_id: item.report_id || '',
          title: item.title || '',
          actor: item.actor || item.actors || '',
          threat: item.threat || item.threats || '',
          severity_score: item.severity_score || 0,
        }))
      : [],
    honorable_mentions: data.honorable_mentions || [],
    platform_stats: data.platform_stats || {
      reports_this_week: data.total_threats || 0,
      cves_tracked: 0,
      iocs_extracted: 0,
    },
    ioc_summary: data.ioc_summary || undefined,
    mitre_coverage: data.mitre_coverage || undefined,
  };
};

// =============================================================================
// HELPER COMPONENTS
// =============================================================================

const TrendIcon: React.FC<{ trend: string }> = ({ trend }) => {
  switch (trend) {
    case 'increasing':
      return <TrendingUpIcon fontSize="small" color="error" />;
    case 'decreasing':
      return <TrendingDownIcon fontSize="small" color="success" />;
    default:
      return <TrendingFlatIcon fontSize="small" color="action" />;
  }
};

const SeverityChip: React.FC<{ severity: string; cvss?: number }> = ({ severity, cvss }) => {
  const colorMap: Record<string, 'error' | 'warning' | 'info' | 'success' | 'default'> = {
    CRITICAL: 'error',
    HIGH: 'warning',
    MEDIUM: 'info',
    LOW: 'success',
  };

  return (
    <Chip
      label={cvss ? `${severity} (${cvss})` : severity}
      color={colorMap[severity] || 'default'}
      size="small"
      sx={{ fontWeight: 'bold' }}
    />
  );
};

const SeverityBar: React.FC<{ breakdown: Record<string, number>; total: number }> = ({
  breakdown,
  total,
}) => {
  if (total === 0) return null;

  const colors: Record<string, string> = {
    CRITICAL: '#ff5c5c',
    HIGH: cyber.accent.coral,
    MEDIUM: cyber.accent.amber,
    LOW: cyber.accent.green,
  };

  return (
    <Box sx={{ display: 'flex', height: 8, borderRadius: 1, overflow: 'hidden', mb: 1 }}>
      {['CRITICAL', 'HIGH', 'MEDIUM', 'LOW'].map((severity) => {
        const count = breakdown[severity] || 0;
        const percentage = (count / total) * 100;
        if (percentage === 0) return null;
        return (
          <Tooltip key={severity} title={`${severity}: ${count}`}>
            <Box
              sx={{
                width: `${percentage}%`,
                backgroundColor: colors[severity],
              }}
            />
          </Tooltip>
        );
      })}
    </Box>
  );
};

const IOCTypeChip: React.FC<{ type: string }> = ({ type }) => {
  const colorMap: Record<string, 'error' | 'warning' | 'info' | 'success' | 'default'> = {
    domain: 'info',
    ip: 'warning',
    hash: 'error',
    url: 'success',
    email: 'default',
  };
  return <Chip label={type} color={colorMap[type] || 'default'} size="small" />;
};

const TACTIC_COLORS: Record<string, string> = {
  'initial-access': '#e53935',
  execution: '#d81b60',
  persistence: '#8e24aa',
  'privilege-escalation': '#5e35b1',
  'defense-evasion': '#3949ab',
  'credential-access': '#1e88e5',
  discovery: '#039be5',
  'lateral-movement': '#00acc1',
  collection: '#00897b',
  'command-and-control': '#43a047',
  exfiltration: '#7cb342',
  impact: '#c0ca33',
  'resource-development': '#fdd835',
  reconnaissance: '#ffb300',
};

// =============================================================================
// MAIN COMPONENT
// =============================================================================

export const WeeklyBriefingPage: React.FC = () => {
  const { authenticatedFetcher } = useApi();

  const [selectedDate, setSelectedDate] = useState<string>('latest');
  const [briefingList, setBriefingList] = useState<BriefingInfo[]>([]);
  const [listError, setListError] = useState<Error | null>(null);
  const [briefing, setBriefing] = useState<WeeklyBriefing | null>(null);
  const [briefingError, setBriefingError] = useState<Error | null>(null);
  const [loading, setLoading] = useState<boolean>(true);

  useEffect(() => {
    const fetchBriefingList = async () => {
      try {
        const data = await authenticatedFetcher('/api/briefings/list');
        setBriefingList(data as BriefingInfo[]);
      } catch (error) {
        console.error('Error fetching briefing list:', error);
        setListError(error as Error);
      }
    };
    fetchBriefingList();
  }, [authenticatedFetcher]);

  useEffect(() => {
    const fetchBriefing = async () => {
      setLoading(true);
      setBriefingError(null);
      setBriefing(null);
      try {
        const data = await authenticatedFetcher(`/api/briefings/${selectedDate}`);
        const normalized = normalizeBriefing(data);
        setBriefing(normalized);
      } catch (error) {
        console.error(`Error fetching briefing for ${selectedDate}:`, error);
        setBriefingError(error as Error);
      } finally {
        setLoading(false);
      }
    };
    fetchBriefing();
  }, [selectedDate, authenticatedFetcher]);

  const handleDateChange = (event: SelectChangeEvent<string>) => {
    setSelectedDate(event.target.value);
  };

  // =============================================================================
  // RENDER HELPERS
  // =============================================================================

  const renderExecutiveSummary = () => {
    if (!briefing?.executive_summary) return null;

    // Check if this is a legacy report
    const isLegacy = briefing.executive_summary.key_takeaway.includes('Legacy report');

    return (
      <Paper
        elevation={0}
        sx={{
          p: 3,
          mb: 3,
          background: isLegacy
            ? 'linear-gradient(135deg, #616161 0%, #424242 100%)'
            : `linear-gradient(135deg, ${cyber.bgPaper} 0%, #3a2d6e 55%, ${cyber.primary} 130%)`,
          border: `1px solid ${alpha(cyber.primary, 0.35)}`,
          color: 'white',
          borderRadius: 2,
        }}
      >
        <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
          <LightbulbIcon sx={{ mr: 1 }} /> {isLegacy ? 'Legacy Report' : 'Key Takeaway'}
        </Typography>
        <Typography variant="body1" sx={{ mb: 2, fontStyle: 'italic' }}>
          "{briefing.executive_summary.key_takeaway}"
        </Typography>
        {!isLegacy && (
          <>
            <Divider sx={{ my: 2, borderColor: 'rgba(255,255,255,0.2)' }} />
            <Typography variant="subtitle2" sx={{ display: 'flex', alignItems: 'center' }}>
              <ArrowForwardIcon sx={{ mr: 1, fontSize: 18 }} /> Looking Ahead
            </Typography>
            <Typography variant="body2" sx={{ opacity: 0.9 }}>
              {briefing.executive_summary.look_ahead}
            </Typography>
          </>
        )}
      </Paper>
    );
  };

  const renderSnapshotCard = () => {
    if (!briefing?.snapshot) return null;

    return (
      <Card sx={{ height: '100%' }}>
        <CardContent>
          <Typography
            variant="h5"
            component="h2"
            gutterBottom
            sx={{ display: 'flex', alignItems: 'center' }}
          >
            <BoltIcon sx={{ mr: 1 }} /> Weekly Snapshot
          </Typography>

          {/* Top TTPs */}
          <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
            Top Observed TTPs
          </Typography>
          <List dense>
            {briefing.snapshot.top_ttps.map((ttp, index) => (
              <ListItem key={index} sx={{ py: 0.5 }}>
                <ListItemIcon sx={{ minWidth: 32 }}>
                  <SecurityIcon fontSize="small" color="primary" />
                </ListItemIcon>
                <ListItemText
                  primary={
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                      }}
                    >
                      <Typography variant="body2">{ttp.name}</Typography>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        {ttp.frequency > 0 && (
                          <Chip
                            label={ttp.frequency}
                            size="small"
                            variant="outlined"
                            sx={{
                              fontFamily: cyber.fontMono,
                              color: cyber.accent.cyan,
                              borderColor: alpha(cyber.accent.cyan, 0.4),
                            }}
                          />
                        )}
                        <TrendIcon trend={ttp.trend} />
                      </Box>
                    </Box>
                  }
                />
              </ListItem>
            ))}
          </List>

          <Divider sx={{ my: 2 }} />

          {/* Top Sectors */}
          <Typography variant="h6" gutterBottom>
            Most Targeted Sectors
          </Typography>
          <List dense>
            {briefing.snapshot.top_sectors.map((sector, index) => (
              <ListItem key={index} sx={{ py: 0.5 }}>
                <ListItemIcon sx={{ minWidth: 32 }}>
                  <BusinessIcon fontSize="small" color="secondary" />
                </ListItemIcon>
                <ListItemText
                  primary={
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                      }}
                    >
                      <Typography variant="body2">{sector.name}</Typography>
                      {sector.count > 0 && (
                        <Typography
                          variant="caption"
                          sx={{ color: alpha(cyber.accent.cyan, 0.9), fontFamily: cyber.fontMono }}
                        >
                          {sector.count} reports ({sector.percentage}%)
                        </Typography>
                      )}
                    </Box>
                  }
                />
              </ListItem>
            ))}
          </List>

          {/* Emerging Pattern - UPDATED COLORS */}
          {briefing.snapshot.emerging_pattern && (
            <>
              <Divider sx={{ my: 2 }} />
              <Paper
                variant="outlined"
                sx={{
                  p: 2,
                  bgcolor: alpha(cyber.accent.amber, 0.12),
                  borderColor: alpha(cyber.accent.amber, 0.4),
                  borderWidth: 1.5,
                }}
              >
                <Typography
                  variant="subtitle2"
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    mb: 1,
                    color: cyber.accent.amber,
                    fontWeight: 'bold',
                  }}
                >
                  <WarningAmberIcon sx={{ mr: 1, color: cyber.accent.amber }} /> Emerging Pattern
                </Typography>
                <Typography variant="body2" sx={{ color: 'text.primary' }}>
                  {briefing.snapshot.emerging_pattern}
                </Typography>
              </Paper>
            </>
          )}
        </CardContent>
      </Card>
    );
  };

  const renderVulnerabilitySpotlight = () => {
    if (!briefing?.vulnerability_spotlight || briefing.vulnerability_spotlight.total_cves === 0)
      return null;
    const vuln = briefing.vulnerability_spotlight;

    return (
      <Card sx={{ mt: 3 }}>
        <CardContent>
          <Typography
            variant="h5"
            component="h2"
            gutterBottom
            sx={{ display: 'flex', alignItems: 'center' }}
          >
            <BugReportIcon sx={{ mr: 1 }} /> Vulnerability Spotlight
          </Typography>

          {/* Stats Row - Using Box with flexbox for MUI v6 compatibility */}
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 3 }}>
            <Box sx={{ flex: '1 1 calc(50% - 8px)', minWidth: 120 }}>
              <Paper variant="outlined" sx={{ p: 2, textAlign: 'center' }}>
                <Typography variant="h4" color="primary" sx={{ fontFamily: cyber.fontMono }}>
                  {vuln.total_cves}
                </Typography>
                <Typography variant="caption">Total CVEs</Typography>
              </Paper>
            </Box>
            <Box sx={{ flex: '1 1 calc(50% - 8px)', minWidth: 120 }}>
              <Paper variant="outlined" sx={{ p: 2, textAlign: 'center' }}>
                <Typography variant="h4" color="error" sx={{ fontFamily: cyber.fontMono }}>
                  {vuln.severity_breakdown.CRITICAL || 0}
                </Typography>
                <Typography variant="caption">Critical</Typography>
              </Paper>
            </Box>
            <Box sx={{ flex: '1 1 calc(50% - 8px)', minWidth: 120 }}>
              <Paper variant="outlined" sx={{ p: 2, textAlign: 'center' }}>
                <Typography variant="h4" color="warning.main" sx={{ fontFamily: cyber.fontMono }}>
                  {vuln.exploited_in_wild}
                </Typography>
                <Typography variant="caption">Exploited</Typography>
              </Paper>
            </Box>
            <Box sx={{ flex: '1 1 calc(50% - 8px)', minWidth: 120 }}>
              <Paper variant="outlined" sx={{ p: 2, textAlign: 'center' }}>
                <Typography variant="h4" color="info.main" sx={{ fontFamily: cyber.fontMono }}>
                  {vuln.top_vendors_affected.length}
                </Typography>
                <Typography variant="caption">Vendors</Typography>
              </Paper>
            </Box>
          </Box>

          {/* Severity Distribution Bar */}
          <Typography variant="subtitle2" gutterBottom>
            Severity Distribution
          </Typography>
          <SeverityBar breakdown={vuln.severity_breakdown} total={vuln.total_cves} />

          {/* Spotlight CVE */}
          {vuln.spotlight_cve && (
            <Paper variant="outlined" sx={{ p: 2, mt: 2, borderColor: 'error.main', borderWidth: 2 }}>
              <Box
                sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}
              >
                <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center' }}>
                  <StarIcon sx={{ mr: 1, color: 'warning.main' }} />
                  {vuln.spotlight_cve.cve_id}
                </Typography>
                <SeverityChip
                  severity={vuln.spotlight_cve.severity}
                  cvss={vuln.spotlight_cve.cvss_score}
                />
              </Box>
              <Typography variant="body2" sx={{ mb: 2 }}>
                {vuln.spotlight_cve.why_notable}
              </Typography>
              
              {/* Action Required - UPDATED COLORS */}
              <Paper 
                sx={{ 
                  p: 1.5, 
                  bgcolor: (theme) => theme.palette.mode === 'dark'
                    ? alpha('#ff5c5c', 0.12)
                    : alpha('#ff5c5c', 0.08),
                  borderLeft: '4px solid #ff5c5c'
                }}
              >
                <Typography
                  variant="subtitle2"
                  sx={{
                    color: '#ff5c5c',
                    fontWeight: 'bold',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0.5,
                    mb: 0.5
                  }}
                >
                  <WarningAmberIcon sx={{ fontSize: 18, color: '#ff5c5c' }} />
                  Action Required
                </Typography>
                <Typography variant="body2" sx={{ color: 'text.primary' }}>
                  {vuln.spotlight_cve.action_required}
                </Typography>
              </Paper>
              
              {vuln.spotlight_cve.related_reports.length > 0 && (
                <Box sx={{ mt: 2 }}>
                  <Button
                    component={RouterLink}
                    to={`/report/${vuln.spotlight_cve.related_reports[0]}`}
                    size="small"
                    variant="outlined"
                  >
                    View Related Report
                  </Button>
                </Box>
              )}
            </Paper>
          )}

          {/* Top Vendors */}
          {vuln.top_vendors_affected.length > 0 && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="subtitle2" gutterBottom>
                Top Affected Vendors
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                {vuln.top_vendors_affected.map((vendor, index) => (
                  <Chip key={index} label={vendor} size="small" variant="outlined" />
                ))}
              </Box>
            </Box>
          )}
        </CardContent>
      </Card>
    );
  };

  const renderIOCSummary = () => {
    if (!briefing?.ioc_summary) return null;
    const ioc = briefing.ioc_summary;

    return (
      <Card sx={{ mt: 3 }}>
        <CardContent>
          <Typography
            variant="h5"
            component="h2"
            gutterBottom
            sx={{ display: 'flex', alignItems: 'center' }}
          >
            <FingerprintIcon sx={{ mr: 1 }} /> IOC Summary
          </Typography>

          {/* Stats Row */}
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 3 }}>
            <Box sx={{ flex: '1 1 calc(33% - 11px)', minWidth: 120 }}>
              <Paper variant="outlined" sx={{ p: 2, textAlign: 'center' }}>
                <Typography variant="h4" color="primary" sx={{ fontFamily: cyber.fontMono }}>
                  {ioc.total_iocs}
                </Typography>
                <Typography variant="caption">Total IOCs</Typography>
              </Paper>
            </Box>
            <Box sx={{ flex: '1 1 calc(33% - 11px)', minWidth: 120 }}>
              <Paper variant="outlined" sx={{ p: 2, textAlign: 'center' }}>
                <Typography variant="h4" color="warning.main" sx={{ fontFamily: cyber.fontMono }}>
                  {ioc.shared_infrastructure_count}
                </Typography>
                <Typography variant="caption">Shared Infrastructure</Typography>
              </Paper>
            </Box>
            <Box sx={{ flex: '1 1 calc(33% - 11px)', minWidth: 120 }}>
              <Paper variant="outlined" sx={{ p: 2, textAlign: 'center' }}>
                <Typography variant="h4" color="info.main" sx={{ fontFamily: cyber.fontMono }}>
                  {Object.keys(ioc.ioc_type_breakdown).length}
                </Typography>
                <Typography variant="caption">IOC Types</Typography>
              </Paper>
            </Box>
          </Box>

          {/* IOC Type Breakdown */}
          <Typography variant="subtitle2" gutterBottom>
            IOC Type Breakdown
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 3 }}>
            {Object.entries(ioc.ioc_type_breakdown)
              .sort(([, a], [, b]) => b - a)
              .map(([type, count]) => (
                <Chip
                  key={type}
                  label={`${type}: ${count}`}
                  size="small"
                  variant="outlined"
                  sx={{ fontFamily: cyber.fontMono }}
                />
              ))}
          </Box>

          {/* Top IOCs to Monitor */}
          {ioc.top_iocs_to_monitor.length > 0 && (
            <>
              <Typography variant="subtitle2" gutterBottom>
                Top IOCs to Monitor
              </Typography>
              <TableContainer component={Paper} variant="outlined" sx={{ mb: 2 }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Type</TableCell>
                      <TableCell>Value</TableCell>
                      <TableCell>Context</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {ioc.top_iocs_to_monitor.map((item, index) => (
                      <TableRow key={index} hover>
                        <TableCell>
                          <IOCTypeChip type={item.type} />
                        </TableCell>
                        <TableCell
                          sx={{
                            fontFamily: cyber.fontMono,
                            fontSize: '0.8rem',
                            wordBreak: 'break-all',
                          }}
                        >
                          {item.value}
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" color="text.secondary">
                            {item.context}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </>
          )}

          {/* Campaign Links */}
          {ioc.campaign_links.length > 0 && (
            <>
              <Typography variant="subtitle2" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
                <HubIcon sx={{ mr: 1, fontSize: 18 }} /> Campaign Linkage
              </Typography>
              <List dense>
                {ioc.campaign_links.map((link, index) => (
                  <ListItem key={index} sx={{ py: 0.25 }}>
                    <ListItemText
                      primary={
                        <Typography variant="body2" sx={{ fontFamily: cyber.fontMono, fontSize: '0.8rem' }}>
                          {link}
                        </Typography>
                      }
                    />
                  </ListItem>
                ))}
              </List>
            </>
          )}
        </CardContent>
      </Card>
    );
  };

  const renderMITRECoverage = () => {
    if (!briefing?.mitre_coverage) return null;
    const mitre = briefing.mitre_coverage;

    const tacticData = Object.entries(mitre.tactic_distribution)
      .map(([name, count]) => ({ name, count }))
      .sort((a, b) => b.count - a.count);

    return (
      <Card sx={{ mt: 3 }}>
        <CardContent>
          <Typography
            variant="h5"
            component="h2"
            gutterBottom
            sx={{ display: 'flex', alignItems: 'center' }}
          >
            <AccountTreeIcon sx={{ mr: 1 }} /> MITRE ATT&CK Coverage
          </Typography>

          {/* Stats Row */}
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 3 }}>
            <Box sx={{ flex: '1 1 calc(50% - 8px)', minWidth: 120 }}>
              <Paper variant="outlined" sx={{ p: 2, textAlign: 'center' }}>
                <Typography variant="h4" color="primary" sx={{ fontFamily: cyber.fontMono }}>
                  {mitre.total_techniques}
                </Typography>
                <Typography variant="caption">Techniques Observed</Typography>
              </Paper>
            </Box>
            <Box sx={{ flex: '1 1 calc(50% - 8px)', minWidth: 120 }}>
              <Paper variant="outlined" sx={{ p: 2, textAlign: 'center' }}>
                <Typography variant="h4" color="secondary" sx={{ fontFamily: cyber.fontMono }}>
                  {Object.keys(mitre.tactic_distribution).length}
                </Typography>
                <Typography variant="caption">Tactics Covered</Typography>
              </Paper>
            </Box>
          </Box>

          {/* Tactic Distribution Chart */}
          <Typography variant="subtitle2" gutterBottom>
            Tactic Distribution
          </Typography>
          <Box sx={{ width: '100%', height: tacticData.length * 32 + 40, mb: 3 }}>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={tacticData} layout="vertical" margin={{ left: 10, right: 30, top: 5, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" horizontal={false} />
                <XAxis type="number" />
                <YAxis
                  dataKey="name"
                  type="category"
                  width={140}
                  tick={{ fontSize: 11 }}
                />
                <RechartsTooltip
                  formatter={(value: number) => [value, 'Occurrences']}
                />
                <Bar dataKey="count" radius={[0, 4, 4, 0]}>
                  {tacticData.map((entry) => (
                    <Cell
                      key={entry.name}
                      fill={TACTIC_COLORS[entry.name.toLowerCase()] || '#757575'}
                    />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </Box>

          <Divider sx={{ my: 2 }} />

          {/* Top Techniques Table */}
          <Typography variant="subtitle2" gutterBottom>
            Top Techniques
          </Typography>
          <TableContainer component={Paper} variant="outlined" sx={{ mb: 2 }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>ID</TableCell>
                  <TableCell>Technique</TableCell>
                  <TableCell>Tactic</TableCell>
                  <TableCell align="center">Frequency</TableCell>
                  <TableCell align="center">Reports</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {mitre.top_techniques.map((tech) => (
                  <TableRow key={tech.technique_id} hover>
                    <TableCell sx={{ fontFamily: cyber.fontMono, fontWeight: 'bold' }}>
                      {tech.technique_id}
                    </TableCell>
                    <TableCell>{tech.technique_name}</TableCell>
                    <TableCell>
                      <Chip
                        label={tech.tactic}
                        size="small"
                        sx={{
                          bgcolor: TACTIC_COLORS[tech.tactic.toLowerCase()] || '#757575',
                          color: 'white',
                          fontSize: '0.7rem',
                        }}
                      />
                    </TableCell>
                    <TableCell align="center">
                      <Chip label={tech.frequency} size="small" variant="outlined" />
                    </TableCell>
                    <TableCell align="center">{tech.report_count}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          <Divider sx={{ my: 2 }} />

          {/* Attack Chain Summary */}
          <Paper
            variant="outlined"
            sx={{
              p: 2,
              mb: 2,
              bgcolor: alpha(cyber.accent.cyan, 0.12),
              borderColor: alpha(cyber.accent.cyan, 0.4),
              borderWidth: 1.5,
            }}
          >
            <Typography
              variant="subtitle2"
              gutterBottom
              sx={{ fontWeight: 'bold', display: 'flex', alignItems: 'center' }}
            >
              <SecurityIcon sx={{ mr: 1, fontSize: 18, color: cyber.accent.cyan }} />
              Attack Chain Summary
            </Typography>
            <Typography variant="body2" sx={{ color: 'text.primary' }}>
              {mitre.attack_chain_summary}
            </Typography>
          </Paper>

          {/* Defensive Recommendations */}
          {mitre.defensive_recommendations.length > 0 && (
            <>
              <Typography
                variant="subtitle2"
                gutterBottom
                sx={{ fontWeight: 'bold', display: 'flex', alignItems: 'center' }}
              >
                <ShieldIcon sx={{ mr: 1, fontSize: 18, color: cyber.accent.green }} />
                Defensive Recommendations
              </Typography>
              <List dense>
                {mitre.defensive_recommendations.map((rec, index) => (
                  <ListItem key={index} sx={{ py: 0.5 }}>
                    <ListItemIcon sx={{ minWidth: 28 }}>
                      <Typography variant="body2" sx={{ fontWeight: 'bold', color: 'primary.main' }}>
                        {index + 1}.
                      </Typography>
                    </ListItemIcon>
                    <ListItemText
                      primary={<Typography variant="body2">{rec}</Typography>}
                    />
                  </ListItem>
                ))}
              </List>
            </>
          )}
        </CardContent>
      </Card>
    );
  };

  const renderDeepDive = () => {
    if (!briefing?.deep_dive) return null;
    const dd = briefing.deep_dive;

    return (
      <Card sx={{ position: 'sticky', top: 88 }}>
        <CardContent>
          <Typography
            variant="h5"
            component="h2"
            gutterBottom
            sx={{ display: 'flex', alignItems: 'center' }}
          >
            <TargetIcon sx={{ mr: 1 }} /> Top Threat Deep Dive
          </Typography>

          <Typography variant="h6" gutterBottom color="primary.main" sx={{ lineHeight: 1.3 }}>
            {dd.title}
          </Typography>

          <Box sx={{ my: 2 }}>
            <Typography variant="body2" sx={{ mb: 1 }}>
              <strong>Actor:</strong> {dd.actor}
            </Typography>
            <Typography variant="body2" sx={{ mb: 1 }}>
              <strong>Threat:</strong> {dd.threat}
            </Typography>
            <Typography variant="body2" sx={{ mb: 1 }}>
              <strong>Key TTPs:</strong> {dd.key_ttps}
            </Typography>
            <Typography variant="body2">
              <strong>Impact:</strong> {dd.impact}
            </Typography>
          </Box>

          <Paper variant="outlined" sx={{ p: 2, bgcolor: 'background.default', mb: 2 }}>
            <Typography
              variant="subtitle2"
              gutterBottom
              sx={{ display: 'flex', alignItems: 'center' }}
            >
              <AnalystIcon sx={{ mr: 1 }} /> Analyst Note
            </Typography>
            <Typography variant="body2" sx={{ fontStyle: 'italic' }}>
              "{dd.analyst_note}"
            </Typography>
          </Paper>

          {/* Selection Rationale - UPDATED COLORS */}
          {dd.selection_rationale && !dd.selection_rationale.includes('N/A') && (
            <Paper
              variant="outlined"
              sx={{
                p: 2,
                bgcolor: alpha(cyber.primary, 0.12),
                borderColor: alpha(cyber.primary, 0.5),
                borderWidth: 1.5,
                mb: 2,
              }}
            >
              <Typography
                variant="subtitle2"
                gutterBottom
                sx={{
                  color: cyber.primaryLight,
                  fontWeight: 'bold',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.5,
                }}
              >
                <LightbulbIcon sx={{ fontSize: 18, color: cyber.primary }} />
                Why This Threat?
              </Typography>
              <Typography variant="body2" sx={{ color: 'text.primary' }}>
                {dd.selection_rationale}
              </Typography>
            </Paper>
          )}

          <Button
            component={RouterLink}
            to={`/report/${dd.report_id}`}
            variant="contained"
            fullWidth
            disabled={!dd.report_id}
          >
            View Full Analysis
          </Button>
        </CardContent>
      </Card>
    );
  };

  const renderFeed = () => {
    if (!briefing?.feed || briefing.feed.length === 0) return null;

    return (
      <Card sx={{ mt: 3 }}>
        <CardContent>
          <Typography
            variant="h5"
            component="h2"
            gutterBottom
            sx={{ display: 'flex', alignItems: 'center' }}
          >
            <FeedIcon sx={{ mr: 1 }} /> Weekly Report Feed
          </Typography>
          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Report Title</TableCell>
                  <TableCell>Actor(s)</TableCell>
                  <TableCell>Threat(s)</TableCell>
                  <TableCell align="center">Severity</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {briefing.feed.map((item) => (
                  <TableRow key={item.report_id} hover>
                    <TableCell>
                      <Button
                        component={RouterLink}
                        to={`/report/${item.report_id}`}
                        size="small"
                        sx={{
                          textAlign: 'left',
                          justifyContent: 'flex-start',
                          p: 0,
                          textTransform: 'none',
                        }}
                      >
                        {item.title}
                      </Button>
                    </TableCell>
                    <TableCell>{item.actor}</TableCell>
                    <TableCell>{item.threat}</TableCell>
                    <TableCell align="center">
                      {item.severity_score > 0 ? (
                        <Chip
                          label={item.severity_score.toFixed(1)}
                          size="small"
                          color={
                            item.severity_score >= 9
                              ? 'error'
                              : item.severity_score >= 7
                              ? 'warning'
                              : 'default'
                          }
                        />
                      ) : (
                        <Chip label="N/A" size="small" variant="outlined" />
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>
    );
  };

  const renderHonorableMentions = () => {
    if (!briefing?.honorable_mentions || briefing.honorable_mentions.length === 0) return null;

    return (
      <Card sx={{ mt: 3 }}>
        <CardContent>
          <Typography
            variant="h5"
            component="h2"
            gutterBottom
            sx={{ display: 'flex', alignItems: 'center' }}
          >
            <StarIcon sx={{ mr: 1, color: 'warning.main' }} /> Honorable Mentions
          </Typography>
          <List>
            {briefing.honorable_mentions.map((mention, index) => (
              <React.Fragment key={mention.report_id}>
                {index > 0 && <Divider />}
                <ListItem sx={{ flexDirection: 'column', alignItems: 'flex-start', py: 2 }}>
                  <Button
                    component={RouterLink}
                    to={`/report/${mention.report_id}`}
                    sx={{ p: 0, textTransform: 'none', textAlign: 'left', mb: 0.5 }}
                  >
                    <Typography variant="subtitle1" color="primary">
                      {mention.title}
                    </Typography>
                  </Button>
                  <Typography variant="body2" color="text.secondary">
                    {mention.one_liner}
                  </Typography>
                </ListItem>
              </React.Fragment>
            ))}
          </List>
        </CardContent>
      </Card>
    );
  };

  const renderPlatformStats = () => {
    if (!briefing?.platform_stats) return null;
    const stats = briefing.platform_stats;

    return (
      <Paper
        variant="outlined"
        sx={{
          mt: 3,
          p: 2,
          display: 'flex',
          justifyContent: 'space-around',
          flexWrap: 'wrap',
          gap: 2,
        }}
      >
        <Box sx={{ textAlign: 'center' }}>
          <Typography variant="h5" color="primary" sx={{ fontFamily: cyber.fontMono }}>
            {stats.reports_this_week}
          </Typography>
          <Typography variant="caption">Reports Analyzed</Typography>
        </Box>
        {stats.cves_tracked > 0 && (
          <Box sx={{ textAlign: 'center' }}>
            <Typography variant="h5" color="error" sx={{ fontFamily: cyber.fontMono }}>
              {stats.cves_tracked}
            </Typography>
            <Typography variant="caption">CVEs Tracked</Typography>
          </Box>
        )}
        {stats.iocs_extracted > 0 && (
          <Box sx={{ textAlign: 'center' }}>
            <Typography variant="h5" color="info.main" sx={{ fontFamily: cyber.fontMono }}>
              {stats.iocs_extracted}
            </Typography>
            <Typography variant="caption">IOCs Extracted</Typography>
          </Box>
        )}
      </Paper>
    );
  };

  // =============================================================================
  // MAIN RENDER
  // =============================================================================

  const renderContent = () => {
    if (loading) {
      return (
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="50vh">
          <CircularProgress />
        </Box>
      );
    }

    if (briefingError) {
      return <Alert severity="error">Error loading the report: {briefingError.message}</Alert>;
    }

    if (listError) {
      return (
        <Alert severity="error">Error loading the list of reports: {listError.message}</Alert>
      );
    }

    if (!briefing) {
      return <Alert severity="info">No briefing reports found.</Alert>;
    }

    return (
      <>
        {/* Executive Summary Banner */}
        {renderExecutiveSummary()}

        <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
          {/* === LEFT COLUMN === */}
          <Box sx={{ flex: '1 1 600px', minWidth: 0 }}>
            <Stack spacing={0}>
              {renderSnapshotCard()}
              {renderVulnerabilitySpotlight()}
              {renderIOCSummary()}
              {renderMITRECoverage()}
              {renderFeed()}
              {renderHonorableMentions()}
              {renderPlatformStats()}
            </Stack>
          </Box>

          {/* === RIGHT COLUMN (Sticky) === */}
          <Box sx={{ flex: '0 1 400px', minWidth: 300 }}>
            {renderDeepDive()}
          </Box>
        </Box>
      </>
    );
  };

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
      {/* === PAGE HEADER === */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 3,
          flexWrap: 'wrap',
          gap: 2,
        }}
      >
        <Box>
          <Typography
            variant="h4"
            component="h1"
            gutterBottom
            sx={{ display: 'flex', alignItems: 'center' }}
          >
            <ShieldIcon sx={{ mr: 1, fontSize: 36 }} /> Weekly Threat Briefing
          </Typography>
          <Typography variant="subtitle1" color="text.secondary">
            Week of {briefing?.meta?.week_of || '...'} |{' '}
            {briefing?.meta?.total_reports_analyzed || '...'} threats analyzed
          </Typography>
        </Box>

        {/* === DROPDOWN SELECTOR === */}
        <FormControl sx={{ minWidth: 240 }} size="small">
          <InputLabel id="briefing-select-label">Select Week</InputLabel>
          <Select
            labelId="briefing-select-label"
            id="briefing-select"
            value={selectedDate}
            label="Select Week"
            onChange={handleDateChange}
            disabled={loading || briefingList.length === 0}
            startAdornment={<CalendarMonthIcon sx={{ mr: 1, color: 'text.secondary' }} />}
          >
            <MenuItem value="latest">
              <em>Latest Report</em>
            </MenuItem>
            {briefingList.map((info) => (
              <MenuItem key={info.date} value={info.date}>
                Week of {info.date}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>

      {/* === DYNAMIC CONTENT === */}
      {renderContent()}
    </Container>
  );
};

export default WeeklyBriefingPage;